import React, { useState, useEffect, useRef } from "react";
import { type AuthUser } from "wasp/auth";
import { useAuth } from "wasp/client/auth";
import {
  Plus,
  Search,
  Filter,
  User as UserIcon,
  MapPin,
  MoreHorizontal,
  Award,
  TrendingUp,
  CheckCircle,
  XCircle,
  Trash2,
  RotateCcw,
} from "lucide-react";
import { Link } from "react-router-dom";
import DefaultLayout from "../../layout/DefaultLayout";
import { EnhancedActionButton, useResponsiveLayout } from "../../../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../../../components/ui/table-filter-layout";
import {
  AdminFlyoutPane,
  FlyoutSection,
  FlyoutField,
} from "../../components/AdminFlyoutPane";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import LoadingSpinner from "../../layout/LoadingSpinner";
import { toast } from "../../../hooks/use-toast";
import useDebounce from "../../../client/hooks/useDebounce";
import {
  getPaginatedUsers,
  useQuery,
  getUserById,
  getAllLocations,
  updateUserByAdmin,
  updateIsUserAdminById,
  deleteUserById,
  restoreUserById,
  deleteMultipleUsers,
  restoreMultipleUsers,
  resetUserPasswordByAdmin,
  createUserByAdmin,
} from "wasp/client/operations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";

const UsersDashboardPage = ({ user }: { user: AuthUser }) => {
  const { data: currentAuthUser } = useAuth();

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isAdminFilter, setIsAdminFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const { isFilterExpanded, setIsFilterExpanded } = useResponsiveLayout();
  const [isDeletedFilter, setIsDeletedFilter] = useState(false);

  // Flyout & Selection
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<"view" | "edit" | "create">("view");
  const [isCreating, setIsCreating] = useState(false);
  const [showLocationsFlyout, setShowLocationsFlyout] = useState(false);

  // Multi-select
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isBulkActioning, setIsBulkActioning] = useState(false);

  // Edit Form State
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPrimaryLocation, setEditPrimaryLocation] = useState("");
  const [editRole, setEditRole] = useState<"admin" | "moderator" | "user">("user");
  const [newPassword, setNewPassword] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const locationSearchRef = useRef<HTMLInputElement | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const skipPages = currentPage - 1;

  // Data Query
  const { data, isLoading, error, refetch } = useQuery(getPaginatedUsers, {
    skipPages,
    pageSize: itemsPerPage,
    filter: {
      ...(debouncedSearchTerm && { emailContains: debouncedSearchTerm }),
      ...(isAdminFilter === "true" && { isAdmin: true }),
      ...(isAdminFilter === "moderator" && { isModerator: true }),
      ...(isAdminFilter === "false" && { isAdmin: false, isModerator: false }),
      isDeleted: isDeletedFilter,
    },
  });

  const usersData = data as
    | { users: any[]; total: number; page: number; totalPages: number }
    | undefined;
  const hasUsers = !!usersData?.users?.length;

  // Fetch selected user details
  const { data: selectedUser, isLoading: loadingUser } = useQuery(
    getUserById,
    { userId: selectedUserId || "" },
    { enabled: !!selectedUserId, keepPreviousData: true },
  );

  const { data: allLocationsRaw } = useQuery(getAllLocations, {
    page: 1,
    limit: 1000,
  });
  const allLocationsData = allLocationsRaw as
    | { locations: any[]; total: number; page: number; totalPages: number }
    | undefined;

  // Effects
  useEffect(() => {
    setCurrentPage(1);
    setSelectedUserIds(new Set());
  }, [debouncedSearchTerm, isAdminFilter, itemsPerPage, isDeletedFilter]);

  useEffect(() => {
    if (isLocationDropdownOpen) {
      setTimeout(() => locationSearchRef.current?.focus(), 0);
    }
  }, [isLocationDropdownOpen]);

  useEffect(() => {
    if (selectedUser) {
      setEditDisplayName(selectedUser.username || "");
      setEditEmail(selectedUser.email || "");
      setEditPrimaryLocation(selectedUser.primaryLocationId?.toString() || "");
      setEditRole(selectedUser.isAdmin ? "admin" : selectedUser.isModerator ? "moderator" : "user");
    }
  }, [selectedUser]);

  // contentPaddingTop logic
  // Header (approx 6rem) + TableFilterLayout Columns (approx 2.5rem)
  // Collapsed: ~8.5rem + 1rem breathing room = 9.5rem
  // Expanded: ~8.5rem + Filters (~4rem) - adjusted to remove gap
  // Header (approx 6rem) + TableFilterLayout Columns (approx 2.5rem)
  // Collapsed: ~8.5rem + 1rem breathing room = 9.5rem
  // Expanded: ~8.5rem + Filters (~4rem) - adjusted to remove gap
  const contentPaddingTop = isFilterExpanded
    ? "pt-[1rem] md:pt-[12.5rem]"
    : "pt-[1rem] md:pt-[9.5rem]";

  // Multi-select handlers
  const handleToggleSelect = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!usersData?.users) return;
    if (selectedUserIds.size === usersData.users.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(usersData.users.map((u: any) => u.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.size === 0) return;
    if (!confirm(`Are you sure you want to move ${selectedUserIds.size} user(s) to the recycle bin?`)) return;
    setIsBulkActioning(true);
    try {
      await deleteMultipleUsers({ userIds: Array.from(selectedUserIds) });
      await refetch();
      setSelectedUserIds(new Set());
      toast({ title: "Success", description: `${selectedUserIds.size} user(s) moved to recycle bin.` });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to delete users", variant: "destructive" });
    } finally {
      setIsBulkActioning(false);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedUserIds.size === 0) return;
    if (!confirm(`Are you sure you want to restore ${selectedUserIds.size} user(s)?`)) return;
    setIsBulkActioning(true);
    try {
      await restoreMultipleUsers({ userIds: Array.from(selectedUserIds) });
      await refetch();
      setSelectedUserIds(new Set());
      toast({ title: "Success", description: `${selectedUserIds.size} user(s) restored successfully.` });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to restore users", variant: "destructive" });
    } finally {
      setIsBulkActioning(false);
    }
  };

  // Handlers
  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
    setFlyoutMode("view");
  };

  const handleEdit = () => {
    setFlyoutMode("edit");
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    try {
      await updateUserByAdmin({
        userId: selectedUser.id,
        username: editDisplayName || undefined,
        email: editEmail || undefined,
        primaryLocationId: editPrimaryLocation || undefined,
        isAdmin: editRole === "admin",
        isModerator: editRole === "moderator",
      });
      await refetch();
      setFlyoutMode("view");
      toast({ title: "Success", description: "User updated successfully" });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update user",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (confirm("Are you sure you want to move this user to the recycle bin?")) {
      if (!selectedUser) return;
      try {
        await deleteUserById({ userId: selectedUser.id });
        await refetch();
        setFlyoutMode("view");
        setSelectedUserId(null);
        toast({ title: "Success", description: "User moved to recycle bin." });
      } catch (error: any) {
        toast({ title: "Error", description: error.message || "Failed to delete user", variant: "destructive" });
      }
    }
  };

  const handleRestore = async () => {
    if (confirm("Are you sure you want to restore this user?")) {
      if (!selectedUser) return;
      try {
        await restoreUserById({ userId: selectedUser.id });
        await refetch();
        setFlyoutMode("view");
        setSelectedUserId(null);
        toast({ title: "Success", description: "User restored successfully." });
      } catch (error: any) {
        toast({ title: "Error", description: error.message || "Failed to restore user", variant: "destructive" });
      }
    }
  };

  const handleAddUser = () => {
    setSelectedUserId(null);
    setEditDisplayName("");
    setEditEmail("");
    setEditRole("user");
    setFlyoutMode("create");
    setIsCreating(true);
  };

  const handleCreate = async () => {
    if (!editEmail) {
      toast({
        title: "Validation Error",
        description: "Email is required",
        variant: "destructive",
      });
      return;
    }
    try {
      await createUserByAdmin({
        email: editEmail,
        username: editDisplayName || undefined,
        isAdmin: editRole === "admin",
        isModerator: editRole === "moderator",
      });
      await refetch();
      setIsCreating(false);
      setFlyoutMode("view");
      toast({ title: "Success", description: "User created successfully" });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create user",
        variant: "destructive",
      });
    }
  };

  const handleResetPassword = async () => {
    console.log("[Client] Reset password button clicked");

    if (!newPassword || newPassword.length < 8) {
      toast({
        title: "Validation Error",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      console.log("[Client] Password too short");
      return;
    }

    // Temporarily removing confirm to troubleshoot "not responding"
    console.log("[Client] Attempting reset for", selectedUser?.email);

    setIsResettingPassword(true);
    try {
      const result = await resetUserPasswordByAdmin({
        userId: selectedUser!.id,
        password: newPassword,
      });
      console.log("[Client] Reset successful:", result);
      toast({
        title: "Success",
        description: "Password has been successfully updated.",
      });
      setNewPassword("");
    } catch (error: any) {
      console.error("[Client] Reset failed:", error);
      toast({
        title: "Failed to reset password",
        description: error.data?.message || error.message || "Internal Error",
        variant: "destructive",
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Check for changes
  const hasChanges = selectedUser
    ? editDisplayName !== (selectedUser.username || "") ||
    editEmail !== (selectedUser.email || "") ||
    editPrimaryLocation !==
    (selectedUser.primaryLocationId?.toString() || "") ||
    editRole !== (selectedUser.isAdmin ? "admin" : selectedUser.isModerator ? "moderator" : "user")
    : false;

  const approvalScore = selectedUser?.approvalScore || 0;
  const scoreColor =
    approvalScore >= 80
      ? "text-green-600"
      : approvalScore >= 60
        ? "text-yellow-600"
        : "text-red-600";
  const isCurrentUser = currentAuthUser?.id === selectedUser?.id;

  // Navigation
  const currentUserIndex =
    usersData?.users?.findIndex((u) => u.id === selectedUserId) ?? -1;
  const hasPrevious = currentUserIndex > 0;
  const hasNext =
    currentUserIndex >= 0 &&
    currentUserIndex < (usersData?.users?.length ?? 0) - 1;

  const handleNavigatePrevious = () => {
    if (hasPrevious && usersData?.users) {
      setSelectedUserId(usersData.users[currentUserIndex - 1].id);
    }
  };

  const handleNavigateNext = () => {
    if (hasNext && usersData?.users) {
      setSelectedUserId(usersData.users[currentUserIndex + 1].id);
    }
  };

  return (
    <DefaultLayout user={user}>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="hidden md:block fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-border min-h-[6rem] flex flex-col justify-center">
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Users
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Manage user accounts and permissions ({usersData?.total || 0}{" "}
                  total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  className={isFilterExpanded ? "bg-accent" : ""}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </Button>
                <EnhancedActionButton
                  onClick={handleAddUser}
                  icon={Plus}
                  label="Add User"
                />
              </div>
            </div>
          </div>
        </div>

        <TableFilterLayout
          className="hidden md:block fixed top-[calc(var(--header-height)+6rem)] left-[var(--sidebar-width)] right-0 z-20"
          isExpanded={isFilterExpanded}
          columns={[
            {
              title: (
                <div className="flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={usersData?.users ? selectedUserIds.size === usersData.users.length && usersData.users.length > 0 : false}
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                </div>
              ),
              span: 1,
            },
            { title: "User Details", span: 5 },
            { title: "Primary Location", span: 4 },
            { title: "Status", span: 2 },
          ]}
          filters={[
            {
              span: 6,
              content: (
                <form
                  autoComplete="off"
                  onSubmit={(e) => e.preventDefault()}
                  className="relative"
                >
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                  <input
                    id="admin-user-search-filter"
                    name="admin-user-search-filter"
                    type="search"
                    placeholder="Search users by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoComplete="no-autocomplete-please"
                    className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mr-4"
                  />
                </form>
              ),
            },
            {
              span: 4,
              content: (
                <select
                  value={isDeletedFilter ? "deleted" : "active"}
                  onChange={(e) => setIsDeletedFilter(e.target.value === "deleted")}
                  className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mr-4"
                >
                  <option value="active">Active Users</option>
                  <option value="deleted">Recycle Bin</option>
                </select>
              ),
            },
            {
              span: 2,
              content: (
                <select
                  value={isAdminFilter}
                  onChange={(e) => setIsAdminFilter(e.target.value)}
                  className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mr-4"
                >
                  <option value="all">All</option>
                  <option value="true">Admin</option>
                  <option value="moderator">Moderator</option>
                  <option value="false">Regular</option>
                </select>
              ),
            },
          ]}
        />

        {/* Scrollable Content */}
        <div
          className={`flex-1 overflow-y-auto transition-[padding] duration-300 ease-in-out ${contentPaddingTop}`}
        >
          {isLoading && <LoadingSpinner />}
          {error && <div className="text-red-500 p-4">{error.message}</div>}

          {usersData?.users && usersData.users.length > 0 ? (
            <div className="w-full">
              {/* Mobile Card View would be here but omitting for brevity/md-focus, jumping to table view */}
              <div className="hidden md:block divide-y divide-gray-200">
                {usersData.users.map((user: any) => (
                  <div
                    key={user.id}
                    className={`admin-hoverable py-3 transition-colors cursor-pointer ${selectedUserIds.has(user.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    onClick={() => handleUserClick(user.id)}
                  >
                    <div className="grid grid-cols-12 gap-4 items-center px-6">
                      {/* Checkbox */}
                      <div className="col-span-1">
                        <div
                          className="flex items-center justify-center"
                          onClick={(e) => handleToggleSelect(user.id, e)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.has(user.id)}
                            onChange={() => { }}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                        </div>
                      </div>

                      {/* User Details */}
                      <div className="col-span-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <UserIcon className="w-4 h-4 text-gray-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {user.email}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.username || "No username"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Primary Location */}
                      <div className="col-span-4">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {user.primaryLocation?.name || "None"}
                        </span>
                      </div>

                      {/* Status */}
                      <div className="col-span-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {user.isAdmin ? "Admin" : user.isModerator ? "Moderator" : "User"}
                            {user.isDeleted && <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Deleted</span>}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleUserClick(user.id)}
                              >
                                View Details
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {usersData.totalPages > 1 && (
                <div className="flex items-center justify-between py-4 border-t px-6">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, usersData.total)} of{" "}
                    {usersData.total} users
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCurrentPage((prev) => Math.max(1, prev - 1));
                        setSelectedUserIds(new Set());
                      }}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Page {currentPage} of {usersData.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCurrentPage((prev) =>
                          Math.min(usersData.totalPages, prev + 1),
                        );
                        setSelectedUserIds(new Set());
                      }}
                      disabled={currentPage === usersData.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No users found</div>
          )}
        </div>

        {/* Bulk Action Floating Bar */}
        {selectedUserIds.size > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 dark:bg-gray-800 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-4 border border-gray-700">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={usersData?.users ? selectedUserIds.size === usersData.users.length : false}
                onChange={handleSelectAll}
                className="h-4 w-4 rounded border-gray-500 text-blue-500 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-sm font-medium">
                {selectedUserIds.size} selected
              </span>
            </div>
            <div className="w-px h-6 bg-gray-600" />
            {isDeletedFilter ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBulkRestore}
                disabled={isBulkActioning}
                className="text-green-400 hover:text-green-300 hover:bg-green-900/30 gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                {isBulkActioning ? "Restoring..." : "Restore All"}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBulkDelete}
                disabled={isBulkActioning}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/30 gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {isBulkActioning ? "Deleting..." : "Delete All"}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedUserIds(new Set())}
              className="text-gray-400 hover:text-gray-200 hover:bg-gray-700"
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Flyout Pane */}
        <AdminFlyoutPane
          open={!!selectedUserId || isCreating}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedUserId(null);
              setIsCreating(false);
              setFlyoutMode("view");
            }
          }}
          title={
            flyoutMode === "create"
              ? "Add New User"
              : flyoutMode === "view"
                ? "View User"
                : "Edit User"
          }
          width="2xl"
          mode={flyoutMode}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          headerActions={
            flyoutMode === "create" ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsCreating(false);
                    setFlyoutMode("view");
                  }}
                  className="w-[110px]"
                >
                  Cancel
                </Button>
                <Button onClick={handleCreate} size="sm" className="w-[110px]">
                  Create
                </Button>
              </>
            ) : flyoutMode === "view" ? (
              <>
                {selectedUser?.deletedAt ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRestore}
                    className="w-[110px]"
                  >
                    Restore
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                  >
                    Delete
                  </Button>
                )}
                <Button onClick={handleEdit} size="sm" className="w-[110px]" disabled={!!selectedUser?.deletedAt}>
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFlyoutMode("view")}
                  className="w-[110px]"
                >
                  {hasChanges ? "Cancel" : "Close"}
                </Button>
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="w-[110px]"
                  disabled={!hasChanges}
                >
                  Save
                </Button>
              </>
            )
          }
        >
          {loadingUser && !selectedUser && flyoutMode !== "create" ? (
            <LoadingSpinner />
          ) : selectedUser || flyoutMode === "create" ? (
            <>
              <div className="border border-gray-200 dark:border-gray-700">
                <div
                  className="grid"
                  style={{ gridTemplateColumns: "30% 70%" }}
                >
                  {/* User Information Section */}
                  <div className="col-span-2 border-b border-gray-200 dark:border-gray-700 py-3 px-4 bg-gray-50 dark:bg-gray-800">
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                      USER INFORMATION
                    </h3>
                  </div>

                  <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Display Name
                    </div>
                  </div>
                  <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {flyoutMode === "edit" || flyoutMode === "create" ? (
                        <Input
                          id="edit-user-display-name"
                          name="edit-user-display-name"
                          value={editDisplayName}
                          onChange={(e) => setEditDisplayName(e.target.value)}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter display name"
                          autoComplete="off"
                        />
                      ) : (
                        <span>{selectedUser?.username || "Not set"}</span>
                      )}
                    </div>
                  </div>

                  <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Email
                    </div>
                  </div>
                  <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {flyoutMode === "edit" || flyoutMode === "create" ? (
                        <Input
                          id="edit-user-email-field"
                          name="edit-user-email-field"
                          type="email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter email"
                          autoComplete="off"
                        />
                      ) : (
                        <span>{selectedUser?.email}</span>
                      )}
                    </div>
                  </div>

                  {/* Locations Section */}
                  <div className="col-span-2 border-b border-gray-200 dark:border-gray-700 py-3 px-4 bg-gray-50 dark:bg-gray-800">
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                      LOCATIONS
                    </h3>
                  </div>

                  <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Primary
                    </div>
                  </div>
                  <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {flyoutMode === "edit" || flyoutMode === "create" ? (
                        <Select
                          value={editPrimaryLocation}
                          onValueChange={setEditPrimaryLocation}
                          onOpenChange={(open) => {
                            setIsLocationDropdownOpen(open);
                            if (!open) setLocationSearch("");
                          }}
                        >
                          <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1">
                            <SelectValue placeholder="Select primary location" />
                          </SelectTrigger>
                          <SelectContent
                            position="item-aligned"
                            className="max-h-72"
                          >
                            <div className="p-2">
                              <Input
                                id="location-search-input"
                                name="location-search-input"
                                value={locationSearch}
                                onChange={(e) =>
                                  setLocationSearch(e.target.value)
                                }
                                placeholder="Search locations..."
                                className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                                ref={locationSearchRef}
                                autoFocus
                                onPointerDown={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                autoComplete="off"
                              />
                            </div>
                            {((allLocationsData as any)?.locations || [])
                              .filter((location: any) => {
                                const name = (
                                  location.name ||
                                  ""
                                ).toLowerCase();
                                return name.includes(
                                  locationSearch.toLowerCase(),
                                );
                              })
                              .map((location: any) => (
                                <SelectItem
                                  key={location.id}
                                  value={location.id}
                                >
                                  {location.name ||
                                    location.locationCode ||
                                    location.id}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-500" />
                          <span>
                            {selectedUser?.primaryLocation?.name ||
                              "No primary location set"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Role
                    </div>
                  </div>
                  <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {flyoutMode === "edit" || flyoutMode === "create" ? (
                        <div className="space-y-1 w-full -my-1">
                          <Select
                            value={editRole}
                            onValueChange={(value) =>
                              setEditRole(value as "admin" | "moderator" | "user")
                            }
                            disabled={isCurrentUser}
                          >
                            <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="moderator">Moderator</SelectItem>
                              <SelectItem value="user">User</SelectItem>
                            </SelectContent>
                          </Select>
                          {isCurrentUser && (
                            <p className="text-xs text-gray-500">
                              You cannot modify your own admin status
                            </p>
                          )}
                        </div>
                      ) : (
                        <span>{selectedUser?.isAdmin ? "Admin" : selectedUser?.isModerator ? "Moderator" : "User"}</span>
                      )}
                    </div>
                  </div>

                  {flyoutMode !== "create" && (
                    <>
                      <div className="border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          Subscribed Locations
                        </div>
                      </div>
                      <div className="py-3 px-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowLocationsFlyout(true)}
                            className="text-xs h-8"
                          >
                            View (
                            {selectedUser?.locationMemberships?.filter(
                              (m: any) =>
                                m.isSubscribed &&
                                m.locationId !== selectedUser?.primaryLocationId,
                            ).length || 0}
                            )
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {flyoutMode !== "create" && (
                <>
                  {/* User Statistics */}
                  <FlyoutSection title="User Statistics">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Award className="w-4 h-4 text-gray-500" />
                          <span className="text-xs font-medium text-gray-500 uppercase">
                            Points
                          </span>
                        </div>
                        <p className="text-2xl font-bold">
                          {selectedUser?.pointsEarned || 0}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-gray-500" />
                          <span className="text-xs font-medium text-gray-500 uppercase">
                            Score
                          </span>
                        </div>
                        <p className={`text-2xl font-bold ${scoreColor}`}>
                          {approvalScore}%
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <FlyoutField
                        label="Approved Posts"
                        value={
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="font-medium text-green-600">
                              {selectedUser?.totalPostsApproved || 0}
                            </span>
                          </div>
                        }
                      />
                      <FlyoutField
                        label="Rejected Posts"
                        value={
                          <div className="flex items-center gap-2">
                            <XCircle className="w-4 h-4 text-red-500" />
                            <span className="font-medium text-red-600">
                              {selectedUser?.totalPostsRejected || 0}
                            </span>
                          </div>
                        }
                      />
                    </div>
                  </FlyoutSection>

                  <FlyoutSection title="Security Control">
                    <div className="p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-900/30 flex flex-col gap-4">
                      <div>
                        <Label className="font-semibold text-red-900 dark:text-red-400">
                          Reset User Password
                        </Label>
                        <p className="text-sm text-red-700 dark:text-red-500">
                          Manually set a new password for this user.
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <Input
                          id="new-user-password-reset"
                          name="new-user-password-reset"
                          type="password"
                          placeholder="New password (8+ chars)"
                          className="flex-1 bg-white dark:bg-sidebar-active border-red-200 dark:border-red-900/50"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          autoComplete="new-password"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleResetPassword}
                          disabled={isResettingPassword}
                        >
                          {isResettingPassword ? "Resetting..." : "Update"}
                        </Button>
                      </div>
                    </div>
                  </FlyoutSection>
                </>
              )}
            </>
          ) : null}
        </AdminFlyoutPane>

        {/* Nested Flyout for Subscribed Locations */}
        <AdminFlyoutPane
          open={showLocationsFlyout}
          onOpenChange={setShowLocationsFlyout}
          title="Subscribed Locations"
          description={`${selectedUser?.username || selectedUser?.email || "User"
            }'s locations`}
          width="lg"
        >
          <div className="space-y-2">
            {selectedUser?.locationMemberships &&
              selectedUser.locationMemberships.length > 0 ? (
              <div className="border border-gray-100 dark:border-gray-800 rounded-none divide-y divide-gray-100 dark:divide-gray-800">
                {selectedUser.locationMemberships
                  .filter(
                    (membership: any) =>
                      membership.isSubscribed &&
                      membership.locationId !== selectedUser.primaryLocationId,
                  )
                  .map((membership: any) => (
                    <div
                      key={membership.id}
                      className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 flex items-center gap-3"
                    >
                      <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {membership.location?.name || membership.locationId}
                        </div>
                      </div>
                    </div>
                  ))}
                {selectedUser.locationMemberships.filter(
                  (m: any) =>
                    m.isSubscribed &&
                    m.locationId !== selectedUser.primaryLocationId,
                ).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No additional subscribed locations
                    </div>
                  )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No subscribed locations
              </div>
            )}
          </div>
        </AdminFlyoutPane>
      </div>
    </DefaultLayout >
  );
};

export default UsersDashboardPage;
